import React, { useState, useEffect, useCallback } from 'react';
import { Button, Segmented, Space, Spin, Typography, message, Row, Col } from 'antd';
import { ReloadOutlined, DownloadOutlined, BarChartOutlined } from '@ant-design/icons';
import {
  getOperationalReport,
  downloadOperationalCsv,
  OperationalReport,
  ReportRange,
} from '../../services/reportsApi';
import MetricCard from '../../components/reports/MetricCard';
import StatusBarChart from '../../components/reports/StatusBarChart';
import TrendSparkline from '../../components/reports/TrendSparkline';

const { Title, Text } = Typography;

// ── WO status display config — mirrors PipelineBar colours ───────────────────

const WO_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new:              { label: 'New',              color: '#1890ff' },
  scheduled:        { label: 'Scheduled',        color: '#722ed1' },
  in_progress:      { label: 'In Progress',      color: '#52c41a' },
  waiting_parts:    { label: 'Waiting Parts',    color: '#fa8c16' },
  waiting_approval: { label: 'Needs Approval',   color: '#ff4d4f' },
  completed:        { label: 'Completed',        color: '#8c8c8c' },
  cancelled:        { label: 'Cancelled',        color: '#d9d9d9' },
};

const RANGE_OPTIONS: Array<{ label: string; value: ReportRange }> = [
  { label: 'Today',     value: 'today' },
  { label: 'This Week', value: 'week'  },
  { label: 'MTD',       value: 'mtd'   },
  { label: 'YTD',       value: 'ytd'   },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtRate(rate: number | null): string {
  if (rate === null) return '—';
  return `${rate.toFixed(1)}%`;
}

function fmtHours(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  return `${hours.toFixed(1)}h`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ReportsPage: React.FC = () => {
  const [range, setRange] = useState<ReportRange>('mtd');
  const [report, setReport] = useState<OperationalReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  const fetchReport = useCallback(async (r: ReportRange) => {
    setLoading(true);
    try {
      const data = await getOperationalReport(r);
      setReport(data);
    } catch (err) {
      console.error('Failed to load operational report:', err);
      message.error('Failed to load report — please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // On-load fetch (D-G-4)
  useEffect(() => {
    fetchReport(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = (value: string | number) => {
    const r = value as ReportRange;
    setRange(r);
    fetchReport(r);
  };

  const handleRefresh = () => {
    fetchReport(range);
  };

  const handleExportCsv = () => {
    setCsvLoading(true);
    // Reset on real completion/error via the service's onFinally callback,
    // not a fixed timer — button reflects true download state.
    downloadOperationalCsv(range, () => setCsvLoading(false));
  };

  // ── Derived data for charts ────────────────────────────────────────────────

  const woStatusItems = report
    ? report.woByStatus.map((r) => ({
        key: r.status,
        label: WO_STATUS_CONFIG[r.status]?.label ?? r.status,
        count: r.count,
        color: WO_STATUS_CONFIG[r.status]?.color ?? '#888',
        drillPath: `/owner/work-orders?status=${r.status}`,
      }))
    : [];

  const techItems = report
    ? report.techProductivity.map((r) => ({
        key: r.technician,
        label: r.technician,
        count: r.completedCount,
        color: '#2563eb',
      }))
    : [];

  const apptSparkPoints = report
    ? report.appointmentVolumeByDay.map((d) => ({
        label: d.date,
        value: d.scheduled + d.completed,
      }))
    : [];

  const utilSparkPoints = report
    ? (() => {
        // Aggregate totalMinutes per day (sum across all techs)
        const byDay = new Map<string, number>();
        for (const row of report.appointmentUtilizationByDay) {
          byDay.set(row.date, (byDay.get(row.date) ?? 0) + row.totalMinutes);
        }
        return Array.from(byDay.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, value]) => ({ label: date, value }));
      })()
    : [];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '16px 12px', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {/* Header */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <BarChartOutlined style={{ fontSize: 22, color: '#2563eb' }} />
            <Title level={2} style={{ margin: 0 }}>Operational Reports</Title>
          </div>
          <Text type="secondary">
            Shop throughput metrics — operational data only. No revenue figures.
          </Text>
        </div>

        {/* Controls */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <Segmented
            data-testid="reports-range-selector"
            options={RANGE_OPTIONS}
            value={range}
            onChange={handleRangeChange}
            size="middle"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            data-testid="reports-refresh-button"
          >
            Refresh
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCsv}
            loading={csvLoading}
            data-testid="reports-csv-button"
          >
            Export CSV
          </Button>
        </div>

        {/* Content */}
        <Spin spinning={loading} tip="Loading report…">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>

            {/* Row 1 — Scalar metrics */}
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={8} md={6} lg={6}>
                <MetricCard
                  title="Jobs In Progress"
                  value={report?.jobsInProgress ?? null}
                  subtitle="Point-in-time"
                  valueColor="#52c41a"
                  testId="metric-jobs-in-progress"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={6}>
                <MetricCard
                  title="Avg Cycle Time"
                  value={report ? fmtHours(report.avgCycleTimeHours) : null}
                  subtitle="WOs completed in range"
                  testId="metric-avg-cycle-time"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={6}>
                <MetricCard
                  title="Conversion Rate"
                  value={report ? fmtRate(report.estimateConversionRate) : null}
                  subtitle={
                    report
                      ? `${report.estimateConversionNumerator} / ${report.estimateConversionDenominator} estimates`
                      : undefined
                  }
                  valueColor="#2563eb"
                  testId="metric-conversion-rate"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={6}>
                <MetricCard
                  title="No-Show Rate"
                  value={report ? fmtRate(report.noShowRate) : null}
                  subtitle={
                    report
                      ? `${report.noShowCount} of ${report.totalAppointments} appts`
                      : undefined
                  }
                  valueColor={
                    report?.noShowRate !== null && (report?.noShowRate ?? 0) > 20
                      ? '#ff4d4f'
                      : '#111827'
                  }
                  testId="metric-no-show-rate"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={6}>
                <MetricCard
                  title="DVI Completion Rate"
                  value={report ? fmtRate(report.dviCompletionRateOverall) : null}
                  subtitle={
                    report
                      ? `${report.dviCompleted} of ${report.dviTotal} DVIs`
                      : undefined
                  }
                  testId="metric-dvi-completion"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={6}>
                <MetricCard
                  title="Parts ETA Adherence"
                  value={report ? fmtRate(report.partsEtaAdherenceRate) : null}
                  subtitle={
                    report
                      ? `${report.partsEtaNumerator} of ${report.partsEtaDenominator} WOs`
                      : undefined
                  }
                  testId="metric-parts-eta"
                />
              </Col>
            </Row>

            {/* Row 2 — Bar charts */}
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <StatusBarChart
                  title="Work Orders by Status"
                  items={woStatusItems}
                  testId="chart-wo-by-status"
                />
              </Col>
              <Col xs={24} md={12}>
                <StatusBarChart
                  title="Tech Productivity (Completed WOs)"
                  items={techItems}
                  testId="chart-tech-productivity"
                />
              </Col>
            </Row>

            {/* Row 3 — Sparklines */}
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <TrendSparkline
                  title="Appointment Volume (by Day)"
                  points={apptSparkPoints}
                  color="#2563eb"
                  testId="sparkline-appt-volume"
                />
              </Col>
              <Col xs={24} md={12}>
                <TrendSparkline
                  title="Appointment Utilization (min/day)"
                  points={utilSparkPoints}
                  color="#52c41a"
                  testId="sparkline-appt-utilization"
                />
              </Col>
            </Row>

          </Space>
        </Spin>
      </Space>
    </div>
  );
};

export default ReportsPage;
